import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiFetch, isApiError } from '../api/client'
import { qk } from '../api/queries'
import type { ActivityResponse, ApiKeysResponse } from '../api/types'
import { useSession } from '../context/SessionContext'
import { Icon } from '../components/Icon'
import { CodeBlock } from '../components/CodeBlock'
import { PageHeader } from '../components/ui'
import { GATEWAY_PUBLIC_URL, mcpEndpoint } from '../api/config'

const installedKey = (ws: string | null) => `symone.gs.installed.${ws ?? 'none'}`

function readInstalled(ws: string | null): boolean {
  try {
    return localStorage.getItem(installedKey(ws)) === '1'
  } catch {
    return false
  }
}

function writeInstalled(ws: string | null) {
  try {
    localStorage.setItem(installedKey(ws), '1')
  } catch {
    // Storage unavailable (private mode) — completion just won't persist.
  }
}

type StepStatus = 'done' | 'current' | 'locked'

function Step({
  n,
  title,
  status,
  children,
  last,
}: {
  n: number
  title: string
  status: StepStatus
  children: ReactNode
  last?: boolean
}) {
  return (
    <li className={`step step--${status}`} aria-current={status === 'current' ? 'step' : undefined}>
      <div className="step-rail" aria-hidden="true">
        <span className="step-dot">{status === 'done' ? <Icon name="check" size={14} /> : n}</span>
        {!last && <span className="step-line" />}
      </div>
      <div className="step-body">
        <h2 className="step-title">
          {title}
          <span className="sr-only"> ({status === 'done' ? 'complete' : status === 'locked' ? 'locked' : 'in progress'})</span>
        </h2>
        {status === 'locked' ? (
          <p className="step-locked"><Icon name="lock" size={14} /> Locked until you finish the previous step</p>
        ) : (
          children
        )}
      </div>
    </li>
  )
}

const INSTALL_TABS = ['MCP config', 'TypeScript', 'Python'] as const
type InstallTab = (typeof INSTALL_TABS)[number]

function installSnippet(tab: InstallTab, ws: string): { label: string; code: string } {
  if (tab === 'MCP config') {
    return {
      label: 'mcp.json',
      code: JSON.stringify(
        {
          mcpServers: {
            symone: {
              type: 'http',
              url: mcpEndpoint(ws),
              headers: { Authorization: 'Bearer <YOUR_SYMONE_API_KEY>' },
            },
          },
        },
        null,
        2,
      ),
    }
  }
  if (tab === 'TypeScript') {
    return {
      label: 'Terminal',
      code: [
        '# The SDK ships via git tag (no public npm registry yet)',
        'git clone --depth 1 --branch sdk-v0.2.0 https://github.com/symone-ai/symone-v2',
        'cd symone-v2/sdks/ts && npm pack',
        'npm install ./symone-sdk-0.2.0.tgz',
      ].join('\n'),
    }
  }
  return {
    label: 'Terminal',
    code: 'pip install "git+https://github.com/symone-ai/symone-v2.git@sdk-v0.2.0#subdirectory=sdks/python"',
  }
}

const FIRST_CALL = `import { Symone } from "@symone/sdk";

const client = new Symone({
  apiKey: process.env.SYMONE_API_KEY,
  baseUrl: "${GATEWAY_PUBLIC_URL}",
});

// Find a tool, then run it.
const [tool] = await client.tools.search("list my calendar events");
const result = await client.tools.execute(tool.name, {});
console.log(result.content);`

export function GettingStartedView() {
  const { workspaceId } = useSession()
  const [installed, setInstalled] = useState(() => readInstalled(workspaceId))
  const [tab, setTab] = useState<InstallTab>('MCP config')

  const keys = useQuery({
    queryKey: qk.apiKeys(workspaceId),
    queryFn: () => apiFetch<ApiKeysResponse>(`/workspaces/${workspaceId}/api-keys`),
    enabled: !!workspaceId,
  })
  const probe = useQuery({
    queryKey: qk.activityProbe(workspaceId),
    queryFn: () => apiFetch<ActivityResponse>('/activity?limit=1&offset=0'),
    refetchInterval: (query) => ((query.state.data?.activity?.length ?? 0) > 0 ? false : 10_000),
  })

  const hasKey = (keys.data?.api_keys?.length ?? 0) > 0
  const keysForbidden = isApiError(keys.error) && keys.error.status === 403
  const hasCall = (probe.data?.activity?.length ?? 0) > 0

  const s1: StepStatus = hasKey ? 'done' : 'current'
  const s2: StepStatus = s1 !== 'done' ? 'locked' : installed ? 'done' : 'current'
  const s3: StepStatus = s2 !== 'done' ? 'locked' : hasCall ? 'done' : 'current'
  const snippet = installSnippet(tab, workspaceId ?? '<WORKSPACE_ID>')

  return (
    <div className="page page--narrow">
      <PageHeader
        title="Getting Started"
        description="Three steps from zero to your agent’s first governed tool call."
      />
      <ol className="stepper">
        <Step n={1} title="Create an API key" status={s1}>
          {hasKey ? (
            <p className="muted">
              You have {keys.data?.api_keys?.length} key{keys.data?.api_keys?.length === 1 ? '' : 's'}.{' '}
              <Link to="/api-keys">Manage keys</Link>
            </p>
          ) : keysForbidden ? (
            <p className="muted">Your role can’t create API keys here. Ask a workspace owner or admin for one.</p>
          ) : (
            <>
              <p>API keys authenticate your agent against this workspace. You’ll only see the key once.</p>
              <Link to="/api-keys" className="btn btn--primary">
                <Icon name="key" size={14} /> Create API key
              </Link>
            </>
          )}
        </Step>

        <Step n={2} title="Install the SDK or MCP config" status={s2}>
          <p>Point any MCP client at your workspace endpoint, or install a typed SDK.</p>
          <div className="seg" role="tablist" aria-label="Install method">
            {INSTALL_TABS.map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                className="seg-btn"
                onClick={() => setTab(t)}
              >
                {t}
              </button>
            ))}
          </div>
          <CodeBlock code={snippet.code} label={snippet.label} />
          {!installed && (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => {
                writeInstalled(workspaceId)
                setInstalled(true)
              }}
            >
              I’ve installed it
            </button>
          )}
        </Step>

        <Step n={3} title="Make your first tool call" status={s3} last>
          {hasCall ? (
            <p className="muted">
              First call received. <Link to="/logs">View it in Logs</Link>
            </p>
          ) : (
            <>
              <p>Run a tool. This step completes automatically when the call shows up in your logs.</p>
              <CodeBlock code={FIRST_CALL} label="first-call.ts" />
              <p className="waiting"><span className="spinner" aria-hidden="true" /> Waiting for your first call…</p>
            </>
          )}
        </Step>
      </ol>
    </div>
  )
}
